import { Position, ZStratum } from "@spscommerce/ds-shared";

const NAVBAR_HEIGHT = 60;

function px(num: number): string {
    return typeof num === "undefined"
        ? "inherit"
        : `${num}px`;
}

export function flipPosition(position: Position): Position {
    switch (position) {
        case Position.TOP_LEFT:
            return Position.BOTTOM_LEFT;
        case Position.TOP_MIDDLE:
            return Position.BOTTOM_MIDDLE;
        case Position.TOP_RIGHT:
            return Position.BOTTOM_RIGHT;
        case Position.RIGHT_TOP:
            return Position.LEFT_TOP;
        case Position.RIGHT_MIDDLE:
            return Position.LEFT_MIDDLE;
        case Position.RIGHT_BOTTOM:
            return Position.LEFT_BOTTOM;
        case Position.BOTTOM_RIGHT:
            return Position.TOP_RIGHT;
        case Position.BOTTOM_MIDDLE:
            return Position.TOP_MIDDLE;
        case Position.BOTTOM_LEFT:
            return Position.TOP_LEFT;
        case Position.LEFT_BOTTOM:
            return Position.RIGHT_BOTTOM;
        case Position.LEFT_MIDDLE:
            return Position.RIGHT_MIDDLE;
        case Position.LEFT_TOP:
            return Position.RIGHT_TOP;
    }
}

function calculatePosition(
    position: Position,
    containerBounds: ClientRect | DOMRect,
    targetBounds: ClientRect | DOMRect,
    subjectBounds: ClientRect | DOMRect,
    resultantWidth: number,
    offsets: [number, number],
    portalContextElementRef: React.RefObject<HTMLElement>,
    fixed: boolean,
): [number, number, number, number] {
    const portalContextElement = portalContextElementRef && portalContextElementRef.current;
    const scrollY = fixed
        ? 0
        : portalContextElement
        ? portalContextElement.scrollTop
        : window.pageYOffset;
    const scrollX = fixed
        ? 0
        : portalContextElement
        ? portalContextElement.scrollLeft
        : window.pageXOffset;

    const [positionA, positionB] = position.split(" ");

    let top;
    let right;
    let bottom;
    let left;

    switch (positionA) {
        case "top":
            bottom = containerBounds.height - targetBounds.top - scrollY - offsets[0];
            break;
        case "left":
            right = containerBounds.width - targetBounds.left - scrollX - offsets[0];
            break;
        case "right":
            left = targetBounds.right + scrollX - offsets[0];
            break;
        case "bottom":
            top = targetBounds.bottom + scrollY - offsets[0];
    }
    switch (positionB) {
        case "left":
            left = targetBounds.left + scrollX + offsets[1];
            break;
        case "top":
            top = targetBounds.top + scrollY + offsets[1];
            break;
        case "middle":
            if (
                positionA === "top" ||
                positionA === "bottom"
            ) {
                left =
                    targetBounds.left +
                    scrollX +
                    targetBounds.width / 2 -
                    resultantWidth / 2 +
                    offsets[1];
            } else if (
                positionA === "left" ||
                positionA === "right"
            ) {
                top =
                    targetBounds.top +
                    scrollY +
                    targetBounds.height / 2 -
                    subjectBounds.height / 2 +
                    offsets[1];
            }
            break;
        case "bottom":
            bottom = containerBounds.height - targetBounds.bottom - scrollY + offsets[1];
            break;
        case "right":
            right = containerBounds.width - targetBounds.right - scrollX + offsets[1];
    }

    return [top, right, bottom, left];
}

export function getPosition(
    subjectRef: React.RefObject<HTMLElement>,
    targetRef: React.RefObject<HTMLElement>,
    position: Position,
    {
        altPosition,
        setMinWidth = false,
        setWidth = false,
        portalContextElementRef,
        fixed = false,
        offsets = [0, 0]
    }: {
        altPosition?: Position,
        setMinWidth?: boolean,
        setWidth?: boolean,
        portalContextElementRef?: React.RefObject<HTMLElement>,
        fixed?: boolean,
        offsets?: [number, number],
    }
): [React.CSSProperties, boolean] {
    if (subjectRef.current && targetRef.current) {
        const viewportHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
        const viewportWidth = document.documentElement.getBoundingClientRect().width;

        const container = portalContextElementRef && portalContextElementRef.current
            ? portalContextElementRef.current : document.documentElement;
        const containerBounds = fixed
            ? {
                width: viewportWidth,
                height: viewportHeight,
                top: 0,
                left: 0,
                bottom: viewportHeight,
                right: viewportWidth,
            }
            : container.getBoundingClientRect();
        const subjectBounds = subjectRef.current.getBoundingClientRect();
        const targetBounds = targetRef.current.getBoundingClientRect();

        const [primaryPosition] = position.split(" ");

        if (
            altPosition && (
                (primaryPosition === "top" && targetBounds.top - subjectBounds.height < NAVBAR_HEIGHT)
                || (primaryPosition === "right" && targetBounds.right + subjectBounds.width > viewportWidth)
                || (primaryPosition === "bottom" && targetBounds.bottom + subjectBounds.height > viewportHeight)
                || (primaryPosition === "left" && targetBounds.left - subjectBounds.width < 0)
            )
        ) {
            position = altPosition;
        }

        const resultantWidth = setWidth
            ? targetBounds.width
            : (
                setMinWidth
                ? Math.max(targetBounds.width, subjectBounds.width)
                : subjectBounds.width
            );

        const [top, right, bottom, left] = calculatePosition(
            position,
            containerBounds,
            targetBounds,
            subjectBounds,
            resultantWidth,
            offsets,
            portalContextElementRef,
            fixed,
        );

        const styles: React.CSSProperties = {
            top: px(top),
            right: px(right),
            bottom: px(bottom),
            left: px(left),
        };

        if (fixed) {
            styles.position = "fixed";
            styles.zIndex = ZStratum.BAR;
        }

        if (setMinWidth) {
            styles.minWidth = `${targetBounds.width}px`;
        }

        if (setWidth) {
            styles.width = `${targetBounds.width}px`;
        }

        return [styles, position === altPosition];
    }
    return [{}, false];
}
